import { useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "@/config";
import { Phone, PhoneCall, PhoneOff, Clock, User, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";

const http = axios.create({ baseURL: API_URL, withCredentials: true });

interface Lead {
    cust_leadid: number;
    cust_fname: string;
    cust_lname: string;
    cust_email: string;
    cust_phoneno: string | null;
    cust_lead_type: string | null;
    cust_status: string | null;
    cust_dealer_id: number;
    cust_lead_created_at: string | null;
    lead_notes: string | null;
}

interface Dealer { id: number; company: string; }

const STATUS_COLOR: Record<string, string> = {
    NEW:          "bg-blue-100 text-blue-700",
    CONTACTED:    "bg-orange-100 text-orange-700",
    SCHEDULED:    "bg-violet-100 text-violet-700",
    HOT:          "bg-red-100 text-red-700",
    WARM:         "bg-yellow-100 text-yellow-700",
    COLD:         "bg-gray-100 text-gray-500",
    CONVERTED:    "bg-green-100 text-green-700",
    LOST:         "bg-gray-100 text-gray-400",
};

const PRIORITY_ORDER = ["HOT", "NEW", "WARM", "CONTACTED", "SCHEDULED", "COLD", "CONVERTED", "LOST"];

export default function BdcCallsPage() {
    const [leads,    setLeads]    = useState<Lead[]>([]);
    const [dealers,  setDealers]  = useState<Dealer[]>([]);
    const [loading,  setLoading]  = useState(true);
    const [filter,   setFilter]   = useState<string>("ALL");
    const [dealerF,  setDealerF]  = useState<number | "ALL">("ALL");
    const [search,   setSearch]   = useState("");

    useEffect(() => {
        Promise.all([
            http.get("/bdc/leads").then(r => r.data).catch(() => []),
            http.get("/bdc/dealers").then(r => r.data).catch(() => []),
        ]).then(([l, d]) => {
            setLeads(l);
            setDealers(d);
        }).finally(() => setLoading(false));
    }, []);

    const sorted = [...leads].sort((a, b) => {
        const ai = PRIORITY_ORDER.indexOf(a.cust_status ?? "NEW");
        const bi = PRIORITY_ORDER.indexOf(b.cust_status ?? "NEW");
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

    const visible = sorted.filter((l) => {
        const matchStatus = filter === "ALL" || l.cust_status === filter || (!l.cust_status && filter === "NEW");
        const matchDealer = dealerF === "ALL" || l.cust_dealer_id === dealerF;
        const q = search.toLowerCase();
        const matchSearch = !q || `${l.cust_fname} ${l.cust_lname} ${l.cust_email} ${l.cust_phoneno ?? ""}`.toLowerCase().includes(q);
        return matchStatus && matchDealer && matchSearch;
    });

    const counts = {
        hot:       leads.filter(l => l.cust_status === "HOT").length,
        new:       leads.filter(l => !l.cust_status || l.cust_status === "NEW").length,
        contacted: leads.filter(l => l.cust_status === "CONTACTED").length,
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Call Queue</h1>
                <p className="text-sm text-muted-foreground mt-0.5">Outbound lead calls — sorted by priority</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
                {[
                    { label: "Hot Leads",   value: counts.hot,       icon: PhoneCall, color: "text-red-500",    bg: "bg-red-50"    },
                    { label: "New Leads",   value: counts.new,       icon: Phone,     color: "text-blue-500",   bg: "bg-blue-50"   },
                    { label: "Contacted",   value: counts.contacted, icon: PhoneOff,  color: "text-orange-500", bg: "bg-orange-50" },
                ].map(({ label, value, icon: Icon, color, bg }) => (
                    <Card key={label} className="p-4">
                        <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                            <Icon className={`h-5 w-5 ${color}`} />
                        </div>
                        <p className="text-2xl font-bold">{loading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : value}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
                    </Card>
                ))}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2">
                {["ALL", "HOT", "NEW", "WARM", "CONTACTED", "SCHEDULED", "COLD"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setFilter(s)}
                        className={`px-3 py-1 text-xs rounded-full border transition ${
                            filter === s ? "bg-cyan-600 text-white border-cyan-600" : "border-gray-200 text-gray-500 hover:border-cyan-400"
                        }`}
                    >
                        {s}
                    </button>
                ))}
            </div>

            <div className="flex flex-wrap gap-2 items-center">
                <input
                    type="text"
                    placeholder="Search name, email, phone…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="border rounded-lg px-3 py-1.5 text-sm flex-1 min-w-48 max-w-xs"
                />
                <select
                    value={String(dealerF)}
                    onChange={(e) => setDealerF(e.target.value === "ALL" ? "ALL" : Number(e.target.value))}
                    className="border rounded-lg px-3 py-1.5 text-sm"
                >
                    <option value="ALL">All Dealers</option>
                    {dealers.map((d) => <option key={d.id} value={d.id}>{d.company}</option>)}
                </select>
            </div>

            {/* Call list */}
            <Card className="overflow-hidden">
                {loading ? (
                    <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                ) : visible.length === 0 ? (
                    <div className="py-10 text-center text-sm text-muted-foreground">No leads match the current filter</div>
                ) : (
                    <div className="divide-y">
                        {visible.map((l) => (
                            <div key={l.cust_leadid} className="flex items-center justify-between px-5 py-3 hover:bg-muted/40">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-cyan-50 flex items-center justify-center shrink-0">
                                        <User className="h-4 w-4 text-cyan-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium">{l.cust_fname} {l.cust_lname}</p>
                                        <p className="text-xs text-muted-foreground">{l.cust_email}</p>
                                        {l.lead_notes && <p className="text-xs text-muted-foreground italic truncate max-w-xs">{l.lead_notes}</p>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                    <div className="text-right">
                                        {l.cust_phoneno ? (
                                            <a
                                                href={`tel:${l.cust_phoneno}`}
                                                className="flex items-center gap-1 text-sm font-mono text-cyan-700 hover:underline"
                                            >
                                                <PhoneCall className="h-3 w-3" />
                                                {l.cust_phoneno}
                                            </a>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">No phone</span>
                                        )}
                                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5 justify-end">
                                            <Clock className="h-3 w-3" />
                                            {l.cust_lead_created_at ? new Date(l.cust_lead_created_at).toLocaleDateString() : "—"}
                                        </div>
                                    </div>
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[l.cust_status ?? "NEW"] ?? "bg-gray-100 text-gray-600"}`}>
                                        {l.cust_status ?? "NEW"}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
}
